import { MessageChannel } from 'modules/graphql';
import { WORKFLOW_ACTIONS } from 'modules/workflows/constants';
import { subscriptionUtils } from 'modules/workflows/utils';
import { BaseOption, NestedRadioSelectProps } from 'ui/selects';
import { modifySubscriptionText } from '../ModifySubscriptionEditor.constants';
import { propertyNames } from './SubscriptionPicker.constants';
import { SubscriptionPickerProps } from './SubscriptionPicker.types';

export const parseChannelMessageTypesForSingleSelect = (channelMessageTypes: MessageChannel[]) => {
  const labels: NestedRadioSelectProps['categoryLabels'] = {};
  const options: BaseOption<string>[] = [];

  channelMessageTypes.forEach(
    ({
      id: channelTypeId,
      name: channelTypeName,
      channelType,
      messageMedium: channelTypeMessageMedium,
      messageTypes,
    }) => {
      labels[channelTypeName] = {
        id: `${channelTypeId}-${modifySubscriptionText.channel}`,
        doubleLine: `ID ${channelTypeId} | ${channelTypeMessageMedium} | ${channelType} channel`,
        label: channelTypeName,
      };

      messageTypes.forEach(({ id: messageTypeId, name: messageTypeName }) => {
        options.push({
          categoryId: channelTypeName,
          label: messageTypeName,
          id: `${messageTypeId}-${modifySubscriptionText.type}`,
          value: `${messageTypeId}-${modifySubscriptionText.type}`,
          doubleLine: `ID ${messageTypeId}`,
        });
      });
    },
  );

  return { labels, options };
};

export const parseChannelMessageTypesForMultiSelect = (channelMessageTypes: MessageChannel[]) => {
  return channelMessageTypes.reduce(
    (
      acc: {
        channelOptions: BaseOption<number>[];
        messageOptions: BaseOption<number>[];
        messageCategories: Record<string, string>;
      },
      channel,
    ) => {
      const { id, channelType, messageMedium, name, messageTypes = [] } = channel;

      const newMessageOptions = messageTypes.map(
        (message) =>
          ({
            categoryId: name,
            label: message.name,
            id: `${message.id}`,
            value: message.id,
            doubleLine: `ID ${message.id}`,
          }) as BaseOption<number>,
      );

      const newChannelOption = {
        id: `${id}`,
        value: id,
        doubleLine: `ID ${id} | ${messageMedium} | ${channelType} channel`,
        label: name,
      };

      return {
        ...acc,
        channelOptions: [...acc.channelOptions, newChannelOption],
        messageCategories: { ...acc.messageCategories, [name]: name },
        messageOptions: [...acc.messageOptions, ...newMessageOptions],
      };
    },
    { channelOptions: [], messageOptions: [], messageCategories: {} },
  );
};

/**
 * Converts a single selection to a plural selection if needed
 */
const getPluralSelection = (
  nodeData: SubscriptionPickerProps['nodeData'],
  { singular: singleName, plural: pluralName }: { singular: string; plural: string },
) => {
  const singleValue = nodeData[singleName];
  const pluralValue = nodeData[pluralName];
  if (subscriptionUtils.modifySubscription.isMultiValueShown(singleValue, pluralValue))
    return pluralValue;
  if (singleValue) return [singleValue];
  return [];
};

/**
 * Gets the correct plural selection for the multiselects, for both channeltype
 * and messagetype (in case a user toggles back and forth)
 */
export const getMultiselectSelections = (nodeData: SubscriptionPickerProps['nodeData']) => {
  const messageTypes = getPluralSelection(
    nodeData,
    propertyNames[WORKFLOW_ACTIONS.MODIFY_MESSAGE_TYPE],
  );
  const channelTypes = getPluralSelection(
    nodeData,
    propertyNames[WORKFLOW_ACTIONS.MODIFY_CHANNEL_SUBSCRIPTION],
  );

  return { messageTypes, channelTypes };
};
